import type Phaser from 'phaser';
import { BUILDING_DEFINITIONS, type BuildingTypeId, type Footprint } from '../sim/buildings/definitions';
import { Rng } from '../core/rng';
import { ART_SCALE } from './proceduralArt';

/**
 * TEMPORARY PLACEHOLDER ART — original post-apocalyptic buildings, drawn at
 * boot with Canvas 2D (no external assets). One texture per building type,
 * sized from the configured tile size so buildings always match the grid.
 *
 * Each texture's `origin` (0..1) is the footprint CENTRE on the ground, so the
 * sprite is positioned at the world point of `footprintCenter(...)`.
 */
export interface BuildingTextureInfo {
  readonly key: string;
  readonly originX: number;
  readonly originY: number;
  /** World px the art rises above the footprint's top corner (for culling / overlays). */
  readonly rise: number;
}

const PAD = 4;

export function buildingTextureKey(type: BuildingTypeId): string {
  return `bld-${type}`;
}

/** How tall each building is, in tile-heights (art only). */
const RISE_TILES: Record<BuildingTypeId, number> = {
  command_center: 2.6,
  survivor_housing: 1.5,
  sawmill: 1.6,
  farm: 1.0,
  warehouse: 1.9,
  wall: 1.1,
  scrap_yard: 1.7,
  barracks: 1.9,
  workshop: 1.8,
  gate: 1.3,
  watchtower: 3.4,
  gun_tower: 3.0,
  generator: 1.9,
};

export function generateBuildingArt(scene: Phaser.Scene, tileW: number, tileH: number): Map<BuildingTypeId, BuildingTextureInfo> {
  const out = new Map<BuildingTypeId, BuildingTextureInfo>();
  for (const def of BUILDING_DEFINITIONS.values()) {
    const key = buildingTextureKey(def.id);
    const rise = RISE_TILES[def.id] * tileH;
    const { width: w, height: h } = def.footprint;
    const cw = (w + h) * (tileW / 2) + PAD * 2;
    const ch = (w + h) * (tileH / 2) + rise + PAD * 2;
    const cx = cw / 2;
    const cy = PAD + rise + ((w + h) / 2) * (tileH / 2);
    if (!scene.textures.exists(key)) {
      const tex = scene.textures.createCanvas(key, Math.ceil(cw * ART_SCALE), Math.ceil(ch * ART_SCALE));
      if (!tex) throw new Error(`Could not create texture ${key}`);
      const ctx = tex.getContext();
      ctx.scale(ART_SCALE, ART_SCALE);
      const iso = new LocalIso(cx, cy, tileW / 2, tileH / 2);
      DRAW[def.id](ctx, iso, def.footprint, tileH);
      tex.refresh();
    }
    out.set(def.id, { key, originX: cx / cw, originY: cy / ch, rise });
  }
  return out;
}

/** Thumbnail data URL for the construction bar (the texture's own canvas). */
export function buildingThumbnail(scene: Phaser.Scene, type: BuildingTypeId): string {
  const src = scene.textures.get(buildingTextureKey(type)).getSourceImage() as HTMLCanvasElement;
  return typeof src.toDataURL === 'function' ? src.toDataURL('image/png') : '';
}

// ------------------------------------------------------------------ drawing kit

/** Projects footprint-relative grid coords (gx, gy, centred on the footprint) and height z to canvas px. */
class LocalIso {
  constructor(
    readonly cx: number,
    readonly cy: number,
    readonly hw: number,
    readonly hh: number,
  ) {}

  p(gx: number, gy: number, z = 0): [number, number] {
    return [this.cx + (gx - gy) * this.hw, this.cy + (gx + gy) * this.hh - z];
  }
}

type Pt = [number, number];

function fillPoly(ctx: CanvasRenderingContext2D, pts: Pt[], color: string): void {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

function strokePoly(ctx: CanvasRenderingContext2D, pts: Pt[], color: string, width = 1): void {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.closePath();
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}

interface BoxColors {
  top: string;
  left: string;
  right: string;
  edge?: string;
}

/**
 * Axis-aligned isometric box. (x0..x1, y0..y1) in footprint-relative grid
 * units, from height z0 to z1. Draws the two visible side faces and the top.
 */
function box(ctx: CanvasRenderingContext2D, iso: LocalIso, x0: number, y0: number, x1: number, y1: number, z0: number, z1: number, c: BoxColors): void {
  const L0 = iso.p(x0, y1, z0), B0 = iso.p(x1, y1, z0), R0 = iso.p(x1, y0, z0);
  const L1 = iso.p(x0, y1, z1), B1 = iso.p(x1, y1, z1), R1 = iso.p(x1, y0, z1), T1 = iso.p(x0, y0, z1);
  fillPoly(ctx, [L0, B0, B1, L1], c.left);
  fillPoly(ctx, [B0, R0, R1, B1], c.right);
  fillPoly(ctx, [T1, R1, B1, L1], c.top);
  if (c.edge) {
    strokePoly(ctx, [L0, B0, B1, L1], c.edge, 0.6);
    strokePoly(ctx, [B0, R0, R1, B1], c.edge, 0.6);
    strokePoly(ctx, [T1, R1, B1, L1], c.edge, 0.6);
  }
}

/** Vertical lines across the two visible faces (corrugated metal / planks). */
function ribs(ctx: CanvasRenderingContext2D, iso: LocalIso, x0: number, y0: number, x1: number, y1: number, z0: number, z1: number, n: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 0.7;
  for (let i = 1; i < n; i++) {
    const f = i / n;
    const a = iso.p(x0 + (x1 - x0) * f, y1, z0), b = iso.p(x0 + (x1 - x0) * f, y1, z1);
    const c = iso.p(x1, y1 - (y1 - y0) * f, z0), d = iso.p(x1, y1 - (y1 - y0) * f, z1);
    ctx.beginPath();
    ctx.moveTo(...a);
    ctx.lineTo(...b);
    ctx.moveTo(...c);
    ctx.lineTo(...d);
    ctx.stroke();
  }
}

/** Ground shadow under the footprint. */
function groundShadow(ctx: CanvasRenderingContext2D, iso: LocalIso, a: number, b: number): void {
  ctx.save();
  ctx.globalAlpha = 0.3;
  fillPoly(ctx, [iso.p(-a + 0.15, -b + 0.15), iso.p(a + 0.25, -b + 0.15), iso.p(a + 0.25, b + 0.25), iso.p(-a + 0.15, b + 0.25)], '#0d0f0c');
  ctx.restore();
}

/** Packed-earth / concrete pad covering the footprint. */
function pad(ctx: CanvasRenderingContext2D, iso: LocalIso, a: number, b: number, color: string): void {
  fillPoly(ctx, [iso.p(-a, -b), iso.p(a, -b), iso.p(a, b), iso.p(-a, b)], color);
}

// ------------------------------------------------------------------ buildings

const DRAW: Record<BuildingTypeId, (ctx: CanvasRenderingContext2D, iso: LocalIso, fp: Footprint, th: number) => void> = {
  command_center(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.05, b - 0.05, '#4d4a44');
    // Sandbag ring at the base.
    box(ctx, iso, -a + 0.12, -b + 0.12, a - 0.12, b - 0.12, 0, th * 0.28, { top: '#8b7d5c', left: '#76694c', right: '#5e533c' });
    // Reinforced concrete bunker.
    const s = 0.42;
    box(ctx, iso, -a + s, -b + s, a - s, b - s, 0, th * 1.25, { top: '#8a8780', left: '#77736b', right: '#5d5a54', edge: '#3e3c38' });
    // Hazard stripe band.
    box(ctx, iso, -a + s - 0.01, -b + s - 0.01, a - s + 0.01, b - s + 0.01, th * 0.72, th * 0.86, { top: '#c8642a', left: '#c8642a', right: '#9c4c1f' });
    // Rooftop structure.
    box(ctx, iso, -0.35, -0.55, 0.45, 0.25, th * 1.25, th * 1.65, { top: '#9a968e', left: '#827e76', right: '#66635d', edge: '#3e3c38' });
    // Slit windows.
    ctx.fillStyle = '#20231f';
    for (const f of [0.25, 0.5, 0.75]) {
      const [x, y] = iso.p(-a + s + (2 * (a - s)) * f, b - s, th * 1.0);
      ctx.fillRect(x - 2.5, y - 1, 5, 2.2);
    }
    // Radio mast + dish.
    const [mx, my] = iso.p(0.1, -0.15, th * 1.65);
    ctx.strokeStyle = '#2d2c29';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(mx, my);
    ctx.lineTo(mx, my - th * 0.95);
    ctx.stroke();
    ctx.lineWidth = 0.7;
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.moveTo(mx - 3 + i * 0.6, my - th * 0.3 * i);
      ctx.lineTo(mx + 3 - i * 0.6, my - th * 0.3 * i);
      ctx.stroke();
    }
    ctx.fillStyle = '#e8402f';
    ctx.beginPath();
    ctx.arc(mx, my - th * 0.97, 1.6, 0, Math.PI * 2);
    ctx.fill();
    const [dx, dy] = iso.p(-0.2, 0.15, th * 1.75);
    ctx.fillStyle = '#c9c5bb';
    ctx.beginPath();
    ctx.ellipse(dx, dy, 4.5, 2.6, -0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#5d5a54';
    ctx.stroke();
  },

  survivor_housing(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.08, b - 0.08, '#5a5244');
    // Two weathered shipping containers.
    box(ctx, iso, -a + 0.15, -b + 0.2, a - 0.15, -0.05, 0, th * 0.8, { top: '#8a4b38', left: '#7a3f2f', right: '#5f3124', edge: '#3b1f17' });
    ribs(ctx, iso, -a + 0.15, -b + 0.2, a - 0.15, -0.05, 0, th * 0.8, 9, 'rgba(0,0,0,0.25)');
    box(ctx, iso, -a + 0.15, 0.1, a - 0.15, b - 0.2, 0, th * 0.8, { top: '#4f6e6b', left: '#44605d', right: '#344b49', edge: '#1f2e2d' });
    ribs(ctx, iso, -a + 0.15, 0.1, a - 0.15, b - 0.2, 0, th * 0.8, 9, 'rgba(0,0,0,0.25)');
    // Tarp roof shared across both.
    const r = th * 0.8;
    fillPoly(ctx, [iso.p(-a + 0.05, -b + 0.12, r + 1), iso.p(a - 0.05, -b + 0.12, r + 1), iso.p(a - 0.05, b - 0.12, r + 1), iso.p(-a + 0.05, b - 0.12, r + 1), iso.p(-a + 0.05, 0, r + th * 0.35)], '#6f7a4a');
    fillPoly(ctx, [iso.p(-a + 0.05, 0, r + th * 0.35), iso.p(a - 0.05, 0, r + th * 0.35), iso.p(a - 0.05, b - 0.12, r + 1), iso.p(-a + 0.05, b - 0.12, r + 1)], '#5b6540');
    // Door + window light.
    const [ddx, ddy] = iso.p(a - 0.15, 0.5, 0);
    fillPoly(ctx, [[ddx, ddy], [ddx + 5, ddy - 2.5], [ddx + 5, ddy - 12], [ddx, ddy - 9.5]], '#2a2622');
    const [wx, wy] = iso.p(-0.2, b - 0.2, th * 0.45);
    ctx.fillStyle = '#f2c46b';
    ctx.fillRect(wx - 2, wy - 2, 4, 3);
  },

  sawmill(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.05, b - 0.05, '#6b5a44');
    // Plank shed on the back half.
    box(ctx, iso, -a + 0.15, -b + 0.15, a - 0.2, 0.1, 0, th * 0.85, { top: '#7c5f3e', left: '#6c5034', right: '#523c27', edge: '#2e2116' });
    ribs(ctx, iso, -a + 0.15, -b + 0.15, a - 0.2, 0.1, 0, th * 0.85, 8, 'rgba(0,0,0,0.3)');
    // Rusty corrugated roof (lean-to).
    fillPoly(ctx, [iso.p(-a + 0.05, -b + 0.05, th * 1.3), iso.p(a - 0.1, -b + 0.05, th * 1.3), iso.p(a - 0.1, 0.25, th * 0.82), iso.p(-a + 0.05, 0.25, th * 0.82)], '#8f5a36');
    strokePoly(ctx, [iso.p(-a + 0.05, -b + 0.05, th * 1.3), iso.p(a - 0.1, -b + 0.05, th * 1.3), iso.p(a - 0.1, 0.25, th * 0.82), iso.p(-a + 0.05, 0.25, th * 0.82)], '#4c2f1b', 0.6);
    // Log pile in front.
    for (let i = 0; i < 4; i++) {
      const [lx, ly] = iso.p(-0.6 + i * 0.32, b - 0.35, 0);
      ctx.fillStyle = '#6d4a2a';
      ctx.fillRect(lx - 6, ly - 6 - (i % 2) * 3, 12, 4);
      ctx.fillStyle = '#c49a64';
      ctx.beginPath();
      ctx.arc(lx + 6, ly - 4 - (i % 2) * 3, 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
    // Saw blade.
    const [sx, sy] = iso.p(a - 0.45, 0.45, th * 0.45);
    ctx.fillStyle = '#b9b6ae';
    ctx.beginPath();
    ctx.arc(sx, sy, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#4d4a44';
    ctx.beginPath();
    ctx.arc(sx, sy, 1.4, 0, Math.PI * 2);
    ctx.fill();
  },

  barracks(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.05, b - 0.05, '#5a5648');
    // Sandbag ring along the front edges.
    for (let i = 0; i < 9; i++) {
      const t = i / 8;
      const [x1, y1] = iso.p(-a + 0.15 + t * (2 * a - 0.3), b - 0.18, 1);
      ctx.fillStyle = i % 2 ? '#8d7f5c' : '#9c8d67';
      ctx.beginPath();
      ctx.ellipse(x1, y1 - 2, 5, 3, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // Long olive hut with a curved corrugated roof (Quonset style).
    box(ctx, iso, -a + 0.2, -b + 0.15, a - 0.25, 0.25, 0, th * 0.7, { top: '#5d6a45', left: '#4f5a3a', right: '#3f482e', edge: '#20251a' });
    ribs(ctx, iso, -a + 0.2, -b + 0.15, a - 0.25, 0.25, 0, th * 0.7, 10, 'rgba(0,0,0,0.28)');
    for (let k = 0; k < 4; k++) {
      const z = th * (0.7 + 0.18 * Math.sin(((k + 1) / 5) * Math.PI));
      const yy = -b + 0.15 + ((0.25 + b - 0.15) * (k + 0.5)) / 4;
      ctx.strokeStyle = '#6f7d52';
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(...iso.p(-a + 0.2, yy, z));
      ctx.lineTo(...iso.p(a - 0.25, yy, z));
      ctx.stroke();
    }
    // Door and a painted star.
    fillPoly(ctx, [iso.p(-0.25, 0.25, 0), iso.p(0.25, 0.25, 0), iso.p(0.25, 0.25, th * 0.45), iso.p(-0.25, 0.25, th * 0.45)], '#2b2f22');
    const [sx, sy] = iso.p(a - 0.6, 0.25, th * 0.42);
    ctx.fillStyle = '#d9cfa6';
    ctx.beginPath();
    for (let k = 0; k < 10; k++) {
      const ang = -Math.PI / 2 + (k * Math.PI) / 5;
      const rr = k % 2 ? 2 : 4.5;
      ctx.lineTo(sx + Math.cos(ang) * rr, sy + Math.sin(ang) * rr);
    }
    ctx.closePath();
    ctx.fill();
    // Flag pole.
    const [px, py] = iso.p(-a + 0.35, b - 0.35, 0);
    ctx.strokeStyle = '#2a2a2a';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(px, py);
    ctx.lineTo(px, py - th * 1.7);
    ctx.stroke();
    ctx.fillStyle = '#c8642a';
    ctx.fillRect(px, py - th * 1.7, 9, 6);
  },

  workshop(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.05, b - 0.05, '#55524c');
    // Brick-and-steel shop with a sawtooth roof.
    box(ctx, iso, -a + 0.15, -b + 0.15, a - 0.15, b - 0.2, 0, th * 0.75, { top: '#6e5144', left: '#7b5a49', right: '#5d4336', edge: '#2c1f19' });
    for (let k = 0; k < 3; k++) {
      const x0 = -a + 0.15 + k * ((2 * a - 0.3) / 3);
      const x1 = x0 + (2 * a - 0.3) / 3;
      fillPoly(ctx, [iso.p(x0, -b + 0.15, th * 0.75), iso.p(x1, -b + 0.15, th * 1.15), iso.p(x1, b - 0.2, th * 1.15), iso.p(x0, b - 0.2, th * 0.75)], k % 2 ? '#7d858c' : '#8c949a');
      strokePoly(ctx, [iso.p(x0, -b + 0.15, th * 0.75), iso.p(x1, -b + 0.15, th * 1.15), iso.p(x1, b - 0.2, th * 1.15), iso.p(x0, b - 0.2, th * 0.75)], '#33383c', 0.6);
    }
    // Roll-up door.
    fillPoly(ctx, [iso.p(-0.35, b - 0.2, 0), iso.p(0.35, b - 0.2, 0), iso.p(0.35, b - 0.2, th * 0.55), iso.p(-0.35, b - 0.2, th * 0.55)], '#9aa1a6');
    for (let k = 1; k < 5; k++) {
      ctx.strokeStyle = 'rgba(0,0,0,0.3)';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(...iso.p(-0.35, b - 0.2, (th * 0.55 * k) / 5));
      ctx.lineTo(...iso.p(0.35, b - 0.2, (th * 0.55 * k) / 5));
      ctx.stroke();
    }
    // Gear sign.
    const [gx, gy] = iso.p(a - 0.15, -0.2, th * 0.5);
    ctx.fillStyle = '#f0b43c';
    ctx.beginPath();
    ctx.arc(gx, gy, 4.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#5d4336';
    ctx.beginPath();
    ctx.arc(gx, gy, 1.8, 0, Math.PI * 2);
    ctx.fill();
  },

  scrap_yard(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.05, b - 0.05, '#4f4b45');
    const rng = new Rng(311);
    // Heaps of salvage at the back.
    for (let i = 0; i < 9; i++) {
      const gx = -a + 0.3 + rng.next() * (2 * a - 0.7);
      const gy = -b + 0.25 + rng.next() * 0.7;
      const s0 = 0.18 + rng.next() * 0.16;
      const colors = ['#7b7f84', '#8c5a3b', '#5f6469', '#a06b42', '#6e7378'];
      box(ctx, iso, gx - s0, gy - s0, gx + s0, gy + s0, 0, th * (0.25 + rng.next() * 0.35), {
        top: colors[i % colors.length]!,
        left: '#3f4246',
        right: '#33363a',
        edge: '#1f2124',
      });
    }
    // Sorting shed (open front) with a rusted roof.
    box(ctx, iso, -0.1, 0.05, a - 0.15, b - 0.15, 0, th * 0.75, { top: '#6f7378', left: '#575b60', right: '#46494d', edge: '#24262a' });
    ribs(ctx, iso, -0.1, 0.05, a - 0.15, b - 0.15, 0, th * 0.75, 6, 'rgba(0,0,0,0.3)');
    fillPoly(ctx, [iso.p(-0.2, -0.05, th * 1.05), iso.p(a - 0.05, -0.05, th * 1.05), iso.p(a - 0.05, b - 0.05, th * 0.8), iso.p(-0.2, b - 0.05, th * 0.8)], '#9a5b33');
    strokePoly(ctx, [iso.p(-0.2, -0.05, th * 1.05), iso.p(a - 0.05, -0.05, th * 1.05), iso.p(a - 0.05, b - 0.05, th * 0.8), iso.p(-0.2, b - 0.05, th * 0.8)], '#4e2c18', 0.6);
    // Crane arm with a magnet.
    const base = iso.p(-a + 0.45, b - 0.45, 0);
    const top = iso.p(-a + 0.45, b - 0.45, th * 1.6);
    const tip = iso.p(-0.1, -0.2, th * 1.55);
    ctx.strokeStyle = '#d29a2c';
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(...base);
    ctx.lineTo(...top);
    ctx.lineTo(...tip);
    ctx.stroke();
    ctx.strokeStyle = '#2a2b2d';
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(...tip);
    ctx.lineTo(tip[0], tip[1] + th * 0.55);
    ctx.stroke();
    ctx.fillStyle = '#3a3c40';
    ctx.fillRect(tip[0] - 4, tip[1] + th * 0.55, 8, 3);
  },

  farm(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    // Tilled soil with crop rows.
    pad(ctx, iso, a - 0.08, b - 0.08, '#5e4a33');
    const rng = new Rng(77);
    for (let i = 0; i < 7; i++) {
      const g = -b + 0.35 + i * ((2 * b - 0.7) / 6);
      ctx.strokeStyle = i % 2 ? '#7c8d3c' : '#6a7d33';
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(...iso.p(-a + 0.3, g, 1));
      ctx.lineTo(...iso.p(a - 0.3, g, 1));
      ctx.stroke();
      for (let k = 0; k < 6; k++) {
        const [px, py] = iso.p(-a + 0.4 + rng.next() * (2 * a - 0.8), g, 2);
        ctx.fillStyle = '#93a64a';
        ctx.fillRect(px - 1, py - 3, 2, 3);
      }
    }
    // Scrap fence.
    const z = th * 0.32;
    ctx.strokeStyle = '#6e6153';
    ctx.lineWidth = 1;
    const corners: Pt[] = [iso.p(-a + 0.1, -b + 0.1), iso.p(a - 0.1, -b + 0.1), iso.p(a - 0.1, b - 0.1), iso.p(-a + 0.1, b - 0.1)];
    ctx.beginPath();
    corners.forEach(([x, y], i) => (i ? ctx.lineTo(x, y - z) : ctx.moveTo(x, y - z)));
    ctx.closePath();
    ctx.stroke();
    for (const [x, y] of corners) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y - z - 2);
      ctx.stroke();
    }
    // Small tool shed at the back corner.
    box(ctx, iso, -a + 0.2, -b + 0.2, -a + 0.95, -b + 0.85, 0, th * 0.75, { top: '#8b8378', left: '#6f6a5f', right: '#57534b', edge: '#2e2c28' });
    // Water barrel.
    const [bx, by] = iso.p(a - 0.45, -b + 0.45, 0);
    ctx.fillStyle = '#3f5d6e';
    ctx.fillRect(bx - 3, by - 9, 6, 9);
    ctx.fillStyle = '#5e8195';
    ctx.beginPath();
    ctx.ellipse(bx, by - 9, 3, 1.4, 0, 0, Math.PI * 2);
    ctx.fill();
  },

  warehouse(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.05, b - 0.05, '#55524c');
    // Corrugated steel hangar.
    const H = th * 1.15;
    box(ctx, iso, -a + 0.12, -b + 0.12, a - 0.12, b - 0.12, 0, H, { top: '#7d8a8f', left: '#6a777c', right: '#525d61', edge: '#2b3235' });
    ribs(ctx, iso, -a + 0.12, -b + 0.12, a - 0.12, b - 0.12, 0, H, 12, 'rgba(0,0,0,0.22)');
    // Ridged roof.
    const R = th * 0.55;
    fillPoly(ctx, [iso.p(-a + 0.08, -b + 0.08, H), iso.p(a - 0.08, -b + 0.08, H), iso.p(a - 0.08, 0, H + R), iso.p(-a + 0.08, 0, H + R)], '#8c999e');
    fillPoly(ctx, [iso.p(-a + 0.08, 0, H + R), iso.p(a - 0.08, 0, H + R), iso.p(a - 0.08, b - 0.08, H), iso.p(-a + 0.08, b - 0.08, H)], '#6e7a7f');
    fillPoly(ctx, [iso.p(a - 0.08, -b + 0.08, H), iso.p(a - 0.08, 0, H + R), iso.p(a - 0.08, b - 0.08, H)], '#56616a');
    // Big sliding door with rust streak.
    const d0 = iso.p(a - 0.12, -0.55, 0), d1 = iso.p(a - 0.12, 0.55, 0), d2 = iso.p(a - 0.12, 0.55, H * 0.8), d3 = iso.p(a - 0.12, -0.55, H * 0.8);
    fillPoly(ctx, [d0, d1, d2, d3], '#3c4447');
    ctx.strokeStyle = '#8a5432';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(...iso.p(a - 0.12, 0.2, H * 0.8));
    ctx.lineTo(...iso.p(a - 0.12, 0.25, H * 0.25));
    ctx.stroke();
    // Crates.
    box(ctx, iso, -0.7, b - 0.1, -0.25, b + 0.3, 0, th * 0.35, { top: '#a07a4a', left: '#8a663b', right: '#6c4f2d', edge: '#3a2a17' });
  },

  gate(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    const H = th * 1.05;
    // Two steel posts.
    box(ctx, iso, -a + 0.02, -b + 0.02, -a + 0.22, b - 0.02, 0, H, { top: '#5d6168', left: '#4c5056', right: '#3b3e43', edge: '#1d1f22' });
    box(ctx, iso, a - 0.22, -b + 0.02, a - 0.02, b - 0.02, 0, H, { top: '#5d6168', left: '#4c5056', right: '#3b3e43', edge: '#1d1f22' });
    // Hinged doors: welded scrap panels with hazard stripes.
    box(ctx, iso, -a + 0.22, -0.08, a - 0.22, 0.08, th * 0.05, H * 0.85, { top: '#7c6a4e', left: '#8a5a3b', right: '#6d4730', edge: '#2a1d14' });
    ctx.save();
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const f0 = 0.15 + i * 0.2;
      ctx.strokeStyle = i % 2 ? '#f0b43c' : '#2a2622';
      ctx.beginPath();
      ctx.moveTo(...iso.p(-a + 0.22 + f0 * (2 * a - 0.44), 0.08, H * 0.3));
      ctx.lineTo(...iso.p(-a + 0.22 + (f0 + 0.12) * (2 * a - 0.44), 0.08, H * 0.55));
      ctx.stroke();
    }
    ctx.restore();
  },

  watchtower(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    const top = th * 2.4;
    // Four timber legs with cross braces.
    const legs: [number, number][] = [[-a + 0.3, -b + 0.3], [a - 0.3, -b + 0.3], [a - 0.3, b - 0.3], [-a + 0.3, b - 0.3]];
    ctx.strokeStyle = '#5b4632';
    ctx.lineWidth = 3;
    for (const [gx, gy] of legs) {
      ctx.beginPath();
      ctx.moveTo(...iso.p(gx, gy, 0));
      ctx.lineTo(...iso.p(gx, gy, top));
      ctx.stroke();
    }
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = '#4a3828';
    ctx.beginPath();
    ctx.moveTo(...iso.p(-a + 0.3, b - 0.3, 0));
    ctx.lineTo(...iso.p(a - 0.3, b - 0.3, top * 0.6));
    ctx.moveTo(...iso.p(a - 0.3, b - 0.3, 0));
    ctx.lineTo(...iso.p(a - 0.3, -b + 0.3, top * 0.6));
    ctx.stroke();
    // Platform with plank walls and a tin roof.
    box(ctx, iso, -a + 0.15, -b + 0.15, a - 0.15, b - 0.15, top, top + th * 0.45, { top: '#7a6346', left: '#6b5539', right: '#56442e', edge: '#2a2016' });
    ribs(ctx, iso, -a + 0.15, -b + 0.15, a - 0.15, b - 0.15, top, top + th * 0.45, 6, 'rgba(0,0,0,0.3)');
    box(ctx, iso, -a + 0.05, -b + 0.05, a - 0.05, b - 0.05, top + th * 0.75, top + th * 0.85, { top: '#7d8389', left: '#666c72', right: '#52575c', edge: '#2a2d30' });
    // Roof posts and a rifle barrel.
    ctx.strokeStyle = '#3b3027';
    ctx.lineWidth = 1.2;
    for (const [gx, gy] of [[-a + 0.2, b - 0.2], [a - 0.2, b - 0.2], [a - 0.2, -b + 0.2]] as const) {
      ctx.beginPath();
      ctx.moveTo(...iso.p(gx, gy, top + th * 0.45));
      ctx.lineTo(...iso.p(gx, gy, top + th * 0.75));
      ctx.stroke();
    }
    ctx.strokeStyle = '#1f1d1b';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(...iso.p(0.2, b - 0.15, top + th * 0.55));
    ctx.lineTo(...iso.p(0.2, b + 0.35, top + th * 0.6));
    ctx.stroke();
  },

  gun_tower(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    // Concrete base with sandbags.
    box(ctx, iso, -a + 0.1, -b + 0.1, a - 0.1, b - 0.1, 0, th * 1.1, { top: '#8b8a84', left: '#76756f', right: '#5f5e59', edge: '#2b2b28' });
    box(ctx, iso, -a + 0.3, -b + 0.3, a - 0.3, b - 0.3, th * 1.1, th * 1.9, { top: '#6f747a', left: '#5c6166', right: '#494d52', edge: '#222427' });
    // Slit and a hazard band.
    fillPoly(ctx, [iso.p(-0.4, b - 0.3, th * 1.5), iso.p(0.4, b - 0.3, th * 1.5), iso.p(0.4, b - 0.3, th * 1.6), iso.p(-0.4, b - 0.3, th * 1.6)], '#141516');
    fillPoly(ctx, [iso.p(-a + 0.1, b - 0.1, th * 0.85), iso.p(a - 0.1, b - 0.1, th * 0.85), iso.p(a - 0.1, b - 0.1, th * 1.0), iso.p(-a + 0.1, b - 0.1, th * 1.0)], '#c8642a');
    // Twin barrels.
    ctx.strokeStyle = '#18191a';
    ctx.lineWidth = 2.2;
    for (const off of [-0.12, 0.12]) {
      ctx.beginPath();
      ctx.moveTo(...iso.p(off, b - 0.3, th * 1.7));
      ctx.lineTo(...iso.p(off + 0.1, b + 0.55, th * 1.75));
      ctx.stroke();
    }
    // Power cable.
    ctx.strokeStyle = '#2d2d2d';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(...iso.p(-a + 0.1, b - 0.5, th * 0.3));
    ctx.quadraticCurveTo(...iso.p(-a - 0.3, b, 0), ...iso.p(-a - 0.1, b + 0.3, 0));
    ctx.stroke();
  },

  generator(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    pad(ctx, iso, a - 0.05, b - 0.05, '#4f4c44');
    // Engine housing.
    box(ctx, iso, -a + 0.2, -b + 0.25, a - 0.35, b - 0.2, 0, th * 0.9, { top: '#a2702e', left: '#8a5e25', right: '#6f4b1d', edge: '#2b1d0b' });
    ribs(ctx, iso, -a + 0.2, -b + 0.25, a - 0.35, b - 0.2, 0, th * 0.9, 7, 'rgba(0,0,0,0.25)');
    // Fuel drums.
    for (const [gx, gy] of [[a - 0.2, -0.4], [a - 0.2, 0.15]] as const) {
      box(ctx, iso, gx - 0.15, gy - 0.15, gx + 0.15, gy + 0.15, 0, th * 0.55, { top: '#3f6b55', left: '#355b48', right: '#2a4839', edge: '#132119' });
    }
    // Exhaust stack and a warning light.
    box(ctx, iso, -a + 0.35, -b + 0.35, -a + 0.55, -b + 0.55, th * 0.9, th * 1.7, { top: '#2f2f2f', left: '#3a3a3a', right: '#2a2a2a' });
    const [lx, ly] = iso.p(0.1, 0, th * 0.9);
    ctx.fillStyle = '#ffd36a';
    ctx.beginPath();
    ctx.arc(lx, ly - 3, 2.6, 0, Math.PI * 2);
    ctx.fill();
    // Wires.
    ctx.strokeStyle = '#1f1f1f';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(...iso.p(-a + 0.2, b - 0.3, th * 0.6));
    ctx.quadraticCurveTo(...iso.p(-a - 0.2, b, th * 0.1), ...iso.p(-a + 0.1, b + 0.1, 0));
    ctx.stroke();
  },

  wall(ctx, iso, fp, th) {
    const a = fp.width / 2, b = fp.height / 2;
    groundShadow(ctx, iso, a, b);
    // Scrap barricade: timber core with sheet-metal plates.
    const H = th * 0.85;
    box(ctx, iso, -a + 0.04, -b + 0.04, a - 0.04, b - 0.04, 0, H, { top: '#6f6458', left: '#5f564b', right: '#4b443b', edge: '#26221d' });
    const plates: [number, number, string][] = [[0.05, 0.45, '#7a8085'], [0.55, 0.95, '#8a5a3b']];
    for (const [f0, f1, color] of plates) {
      fillPoly(ctx, [iso.p(-a + 0.04 + f0 * (2 * a - 0.08), b - 0.04, H * 0.15), iso.p(-a + 0.04 + f1 * (2 * a - 0.08), b - 0.04, H * 0.15), iso.p(-a + 0.04 + f1 * (2 * a - 0.08), b - 0.04, H * 0.85), iso.p(-a + 0.04 + f0 * (2 * a - 0.08), b - 0.04, H * 0.85)], color);
    }
    // Spikes on top.
    ctx.fillStyle = '#3d362e';
    for (const [gx, gy] of [[-0.25, -0.25], [0.25, -0.25], [-0.25, 0.25], [0.25, 0.25], [0, 0]] as const) {
      const [x, y] = iso.p(gx, gy, H);
      ctx.beginPath();
      ctx.moveTo(x - 1.6, y);
      ctx.lineTo(x, y - th * 0.3);
      ctx.lineTo(x + 1.6, y);
      ctx.closePath();
      ctx.fill();
    }
  },
};
